import { getCurrentEvent } from "core-integration/src/store/events/reducers";
import { IH2HMatch } from "core-integration/src/store/leagues/types";
import { getPlayerData } from "core-integration/src/store/player/reducers";
import Alert from "plos/src/components/alerts/Alert";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getShortNameFromId } from "../../../../../utils/events";
import { MatchScore } from "../../MatchScore";
import { MatchEntry } from "../MatchEntry";
import WinnerStatus from "../WinnerStatus";
import {
  eventStyles,
  itemStatusContainer,
  knockoutName,
  listItemStyles,
  matchEntryAndScore,
  matchInfo,
  matchItemContent,
} from "./matchItem.css";

interface MatchItemProps {
  selectedEntry: number;
  isCup?: boolean;
  showEvent?: boolean;
  started?: boolean;
  match: IH2HMatch;
  isAllMine: boolean;
}

const MatchItem = ({
  selectedEntry,
  showEvent = false,
  started = false,
  match,
  isAllMine = false,
  isCup = false,
}: MatchItemProps) => {
  const { t } = useTranslation("leaguesAndCups");
  const player = useSelector(getPlayerData);
  const now = useSelector(getCurrentEvent);
  const inProgress = now?.id === match.event && !match.winner;
  const mine =
    !isAllMine &&
    player !== null &&
    (player.entry === match.entry_1_entry ||
      player.entry === match.entry_2_entry);

  const {
    // entry 1
    entry_1_entry: firstEntry,
    entry_1_name: firstEntryName,
    entry_1_player_name: firstPlayerName,
    entry_1_points: firstEntryPoints,
    // entry 2
    entry_2_entry: secondEntry,
    entry_2_player_name: secondEntryPlayerName,
    entry_2_name: secondEntryName,
    entry_2_points: secondEntryPoints,
    is_bye: isBye,
    event,
    winner,
  } = match;

  const firstEntryNumber = firstEntry ?? 0;
  const secondEntryNumber = secondEntry ?? 0;

  const isSelectedEntrySecondEntry = selectedEntry === secondEntry;

  const getLink = (started: boolean, matchEntry: number) => {
    if (started) {
      return `/entry/${matchEntry}/event/${event}`;
    }
    return `/entry/${matchEntry}/history`;
  };

  const [matchEntryOne, matchEntryTwo] = isSelectedEntrySecondEntry
    ? [
        {
          entry: secondEntryNumber,
          playerName: secondEntryPlayerName,
          entryName: secondEntryName,
          points: secondEntryPoints,
          link: getLink(started, secondEntryNumber),
        },
        {
          entry: firstEntryNumber,
          playerName: firstPlayerName,
          entryName: firstEntryName,
          points: firstEntryPoints,
          link: getLink(started, firstEntryNumber),
        },
      ]
    : [
        {
          entry: firstEntryNumber,
          playerName: firstPlayerName,
          entryName: firstEntryName,
          points: firstEntryPoints,
          link: getLink(started, firstEntryNumber),
        },
        {
          entry: secondEntryNumber,
          playerName: secondEntryPlayerName,
          entryName: secondEntryName,
          points: secondEntryPoints,
          link: getLink(started, secondEntryNumber),
        },
      ];

  const itemVariant = mine ? "mine" : "default";
  const koNameVariant = match.event === 38 ? "isFinal" : "default";

  return (
    <li className={listItemStyles[itemVariant]}>
      <div className={matchItemContent}>
        {showEvent && (
          <span className={eventStyles}>{getShortNameFromId(event)}</span>
        )}
        <div className={matchInfo}>
          {match.knockout_name && (
            <div className={knockoutName[koNameVariant]}>
              {match.knockout_name}
            </div>
          )}
          <div className={matchEntryAndScore}>
            <MatchEntry
              link={matchEntryOne.link}
              entry={matchEntryOne.entry}
              entryName={matchEntryOne.entryName}
              playerName={matchEntryOne.playerName}
              align={"right"}
            />
            <MatchScore
              started={started}
              inProgress={inProgress}
              isBye={isBye}
              firstEntryPoints={matchEntryOne.points}
              secondEntryPoints={matchEntryTwo.points}
            />
            <MatchEntry
              link={matchEntryTwo.link}
              entry={matchEntryTwo.entry}
              entryName={matchEntryTwo.entryName}
              playerName={matchEntryTwo.playerName}
              align={"left"}
            />
          </div>
        </div>
      </div>
      <div className={itemStatusContainer}>
        {isBye && (
          <Alert isCentered isInline>
            {t(
              "matchEntryIsBye",
              "{{ firstEntryName }} progressed with a bye due to their GW {{ event }} score",
              { firstEntryName, event: event - 1 },
            )}
          </Alert>
        )}
        {winner && <WinnerStatus match={match} isCup={isCup} />}
      </div>
    </li>
  );
};

export default MatchItem;
